import { h, Fragment } from 'preact'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { useEffect, useRef, useState } from 'preact/compat'
import './styles.scss'
import './assets/theme.scss'
import Slot from './components/Common/Slot'
import SkeletonLoader from './components/Common/SkeletonLoader'
import PaymentDetailsPCIIframe from './components/MfeElements/PaymentDetails/PaymentDetailsPCIIframe'
import PaymentMethods from './components/MfeElements/PaymentMethods'
import PaymentDetails from './components/MfeElements/PaymentDetails'
import PaymentButton from './components/MfeElements/PaymentButton'
import PaymentTimer from './components/MfeElements/PaymentTimer'
import PaymentPricePerPerson from './components/MfeElements/PaymentPricePerPerson'
import PaymentTotalPrice from './components/MfeElements/PaymentTotalPrice'
import PaymentTitle from './components/MfeElements/PaymentTitle'
import PaymentPayLink from './components/MfeElements/PaymentPayLink'
import PaymentQrCode from './components/MfeElements/PaymentPayQrCode'
import {
  dispatchMFEEvent,
  dispatchPaymentDetailsIframeEvent,
} from './helpers/events'
import PaymentNotes from './components/MfeElements/PaymentNotes'
import ErrorMessage from './components/Common/ErrorMessage'
import { withStore } from './hocs/withStore'
import { selectIsPciFormEnabled } from './redux/selectors/evervault'
import useTheme from './hooks/useTheme'
import useToken from './hooks/useToken'
import usePaymentMethods from './hooks/usePaymentMethods'
import useComponents from './hooks/useComponents'
import useSavedCards from './hooks/useSavedCards'
import useCulture from './hooks/useCulture'
import usePaymentBlocked from './hooks/usePaymentBlocked'
import useMetadata, { getMetadataFromString } from './hooks/useMetadata'
import usePaypalStyles from './hooks/usePaypalStyles'
import useAmount from './hooks/useAmount'
import PaymentAcceptedMethods from './components/MfeElements/PaymentAcceptedMethods'
import {
  componentsLoadUpdate,
  isAppLoadedSelector,
  pageStateSelector,
  pageStateUpdate,
} from './redux/slice/pageStateSlice'
import {
  transactionDetailsUpdate,
  hookBillingAddressOverrideUpdate,
  transactionDetailsSelector,
} from './redux/slice/transactionDetailsSlice'
import { mergeBillingAddressOverride } from './helpers/billingAddress'
import { DataDog } from './datadog'

const READINESS_TIMEOUT_MS = 30000

const App = ({
  components,
  token,
  amount,
  isPaymentBlocked = '',
  lang = '',
  availableTenantPaymentMethods = '',
  selectedTenantPaymentMethod = '',
  paypalButtonStyle = '',
  paypalCreditButtonStyle = '',
  showHoursInTimer = '',
  metadata = '',
  showPaymentConfirmation = false,
  loaderImage = '',
  currentTheme,
  attributeThemeValue,
  setCurrentTheme,
  stylesHref,
}) => {
  if (!token) {
    return null
  }
  const [swishQrCode, setSwishQrCode] = useState('')
  const dispatch = useDispatch()
  const transactionDetails = useSelector(transactionDetailsSelector)
  const isAppLoaded = useSelector(isAppLoadedSelector)
  const pageState = useSelector(pageStateSelector)
  // Evervault > PCI > default: PCI iframe only when Evervault isn't active.
  const isPciFormEnabled = useSelector(selectIsPciFormEnabled)
  const iframeRef = useRef(null)

  const {
    register,
    watch,
    formState: { errors },
    control,
    trigger,
    setValue,
    setFocus,
    clearErrors,
    handleSubmit,
    getValues,
  } = useForm({
    mode: 'onTouched',
  })

  document.onkeydown = event => {
    if (event.key === 'Tab') {
      console.log(`Transaction ID: ${transactionDetails?.id}`)
    }
  }

  window.addEventListener('unload', function () {})
  window.addEventListener('beforeunload', function () {})

  useTheme({
    transactionThemeValue: transactionDetails?.theme,
    attributeThemeValue,
    setCurrentTheme,
  })

  usePaymentBlocked(isPaymentBlocked)
  usePaypalStyles({
    paypalButtonStyle,
    paypalCreditButtonStyle,
  })

  const { hasDetailsError } = useToken({
    token,
  })
  const { currentPaymentMethod, setCurrentPaymentMethod } = usePaymentMethods({
    availableTenantPaymentMethods,
    selectedTenantPaymentMethod,
  })
  const elements = useComponents(components)

  // Owns pageState.areCardsLoaded here rather than in the Card button, so that
  // PaymentDetails can become ready for component lists that omit "button".
  // The token scopes the card state to one transaction, so a token swap cannot
  // carry the previous user's cards or readiness into the new one.
  useSavedCards({ token })

  useMetadata(metadata)

  useCulture(lang)

  useAmount(amount)

  useEffect(() => {
    window.tuiPaymentsMFEInstance.setMetadata = data => {
      const value = getMetadataFromString(data)
      dispatch(
        transactionDetailsUpdate({
          metadata: value,
        }),
      )
    }
  }, [])

  useEffect(() => {
    dispatch(
      pageStateUpdate({
        availableComponents: elements,
      }),
    )

    if (elements.length) {
      if (!elements.includes('details')) {
        dispatch(
          componentsLoadUpdate({
            isDetailsReady: true,
          }),
        )
      }

      if (!elements.includes('methods')) {
        dispatch(
          componentsLoadUpdate({
            isPaymentMethodsReady: true,
          }),
        )
      }
    }
  }, [elements])

  const onSelectPaymentMethod = value => {
    setCurrentPaymentMethod(value)

    if (isAppLoaded) {
      dispatchMFEEvent('tui-methods.selected', value)
    }
  }

  useEffect(() => {
    dispatchPaymentDetailsIframeEvent(
      {
        type: 'PaymentsMFERerender',
      },
      iframeRef?.current?.contentWindow,
    )
  })

  const componentsMap = new Map([
    ['title', <PaymentTitle />],
    ['acceptedMethods', <PaymentAcceptedMethods />],
    ['timer', <PaymentTimer showHoursInTimer={showHoursInTimer} />],
    ['totalPrice', <PaymentTotalPrice />],
    ['paymentLink', <PaymentPayLink />],
    ['paymentQrCode', <PaymentQrCode />],
    ['pricePerPerson', <PaymentPricePerPerson />],
    ['methods', <PaymentMethods onSelect={onSelectPaymentMethod} />],
    [
      'details',
      <>
        {isPciFormEnabled && (
          <PaymentDetailsPCIIframe
            iframeRef={iframeRef}
            token={token}
            theme={currentTheme}
            stylesHref={stylesHref}
            paymentMethod={currentPaymentMethod}
          />
        )}

        {(!isPciFormEnabled || currentPaymentMethod !== 'Card') && (
          <PaymentDetails
            register={register}
            watch={watch}
            errors={errors}
            control={control}
            trigger={trigger}
            setValue={setValue}
            setFocus={setFocus}
            swishQrCode={swishQrCode}
            clearErrors={clearErrors}
            paymentMethod={currentPaymentMethod}
            getValues={getValues}
            theme={currentTheme}
          />
        )}
      </>,
    ],
    ['notes', <PaymentNotes register={register} />],
    [
      'button',
      <PaymentButton
        watch={watch}
        errors={errors}
        handleSubmit={handleSubmit}
        paymentMethod={currentPaymentMethod}
        setSwishQrCode={setSwishQrCode}
        showPaymentConfirmation={showPaymentConfirmation}
        loaderImage={loaderImage}
        iframeRef={iframeRef}
        getValues={getValues}
      />,
    ],
  ])

  const getComponent = element => {
    if (componentsMap.has(element)) {
      return componentsMap.get(element)
    }

    return <Slot name={element} />
  }

  const hasAppLoaded = useRef(false)

  useEffect(() => {
    if (isAppLoaded && !hasAppLoaded.current) {
      hasAppLoaded.current = true
      dispatchMFEEvent('tui-payments.ready')

      const overrideValues = window.tuiPaymentsMFEInstance?.onInitialised?.({
        billingAddress: transactionDetails.billingAddress || {},
      })

      const mergedBillingAddress = mergeBillingAddressOverride(
        transactionDetails.billingAddress,
        overrideValues,
      )

      if (mergedBillingAddress) {
        dispatch(
          transactionDetailsUpdate({ billingAddress: mergedBillingAddress }),
        )
        // Persist the override so it survives later card-form writes and is
        // re-applied (override wins) at authorization time.
        dispatch(
          hookBillingAddressOverrideUpdate(overrideValues.billingAddress),
        )
      }
    }
  }, [isAppLoaded])

  const pageStateRef = useRef(pageState)
  useEffect(() => {
    pageStateRef.current = pageState
  }, [pageState])

  useEffect(() => {
    if (isAppLoaded) return

    const timeout = setTimeout(() => {
      if (!hasAppLoaded.current) {
        const { componentsLoad, displayedPaymentMethods } = pageStateRef.current
        DataDog.error(
          'MFE readiness timeout: app stuck in loading state (skeleton loader visible)',
          {
            isConfigReady: componentsLoad.isConfigReady,
            isPaymentMethodsReady: componentsLoad.isPaymentMethodsReady,
            isDetailsReady: componentsLoad.isDetailsReady,
            displayedPaymentMethods,
            timeoutMs: READINESS_TIMEOUT_MS,
          },
        )
      }
    }, READINESS_TIMEOUT_MS)

    return () => clearTimeout(timeout)
  }, [isAppLoaded])

  if (hasDetailsError) {
    return (
      <ErrorMessage
        isShown={true}
        message={'An error has occurred, please refresh this page and retry.'}
      />
    )
  }
  return (
    <>
      {!isAppLoaded && <SkeletonLoader count={20} />}
      <tui-payments-components-wrapper
        className="mfe-component"
        style={{ display: isAppLoaded ? 'block' : 'none' }}
      >
        {elements.map(element => getComponent(element.trim()))}
      </tui-payments-components-wrapper>
    </>
  )
}

App.propTypes = {
  token: PropTypes.string,
  components: PropTypes.string,
  amount: PropTypes.string,
  isPaymentBlocked: PropTypes.string,
  lang: PropTypes.string,
  availableTenantPaymentMethods: PropTypes.string,
  selectedTenantPaymentMethod: PropTypes.string,
  paypalButtonStyle: PropTypes.string,
  paypalCreditButtonStyle: PropTypes.string,
  showHoursInTimer: PropTypes.string,
  showPaymentConfirmation: PropTypes.bool,
  metadata: PropTypes.string,
  loaderImage: PropTypes.string,
  currentTheme: PropTypes.string,
  attributeThemeValue: PropTypes.string,
  setCurrentTheme: PropTypes.func,
  stylesHref: PropTypes.string,
}

export default withStore(App)
